/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║  useVoiceInput — Hook für Spracheingabe via Mikrofon         ║
 * ║                                                              ║
 * ║  Nimmt Audio auf, sendet es an das Backend (Whisper API),    ║
 * ║  und gibt den transkribierten Text zurück.                   ║
 * ║                                                              ║
 * ║  Fixes:                                                      ║
 * ║  - Robustere Base64-Kodierung (FileReader statt btoa)        ║
 * ║  - Besseres Error-Logging                                    ║
 * ║  - Korrekte MIME-Type-Weitergabe                             ║
 * ╚══════════════════════════════════════════════════════════════╝
 */

import { useState, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";

export interface VoiceInputState {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  duration: number; // Sekunden
}

/**
 * Convert a Blob to base64 using FileReader (works for all sizes, no btoa stack overflow)
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

export function useVoiceInput(clientId: string) {
  const [state, setState] = useState<VoiceInputState>({
    isRecording: false,
    isTranscribing: false,
    error: null,
    duration: 0,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const transcribeMutation = trpc.voice.transcribe.useMutation();

  /**
   * Startet die Audioaufnahme.
   */
  const startRecording = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, error: null, duration: 0 }));

      console.log("[Voice] Requesting microphone access...");

      // Mikrofon-Zugriff anfordern
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      console.log("[Voice] Microphone access granted");

      // MediaRecorder erstellen — prefer webm, fallback to mp4
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      console.log("[Voice] Using MIME type:", mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250); // Alle 250ms ein Chunk

      // Timer für Aufnahmedauer
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds += 1;
        setState((prev) => ({ ...prev, duration: seconds }));

        // Max 2 Minuten Aufnahme
        if (seconds >= 120) {
          stopRecording();
        }
      }, 1000);

      setState((prev) => ({ ...prev, isRecording: true }));
    } catch (err) {
      console.error("[Voice] Microphone access error:", err);
      setState((prev) => ({
        ...prev,
        error:
          "Mikrofon-Zugriff verweigert. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.",
      }));
    }
  }, []);

  /**
   * Stoppt die Aufnahme und transkribiert das Audio.
   * Gibt den transkribierten Text zurück.
   */
  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === "inactive") {
        console.warn("[Voice] No active recording to stop");
        resolve(null);
        return;
      }

      // Timer stoppen
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setState((prev) => ({ ...prev, isRecording: false, isTranscribing: true }));

      mediaRecorder.onstop = async () => {
        // Stream-Tracks stoppen
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());

        const audioBlob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType,
        });

        console.log(
          "[Voice] Recording stopped. Blob size:",
          audioBlob.size,
          "bytes, MIME:",
          mediaRecorder.mimeType,
          "Chunks:",
          chunksRef.current.length
        );

        // Mindestgröße prüfen (zu kurze Aufnahmen)
        if (audioBlob.size < 1000) {
          console.warn("[Voice] Recording too short:", audioBlob.size, "bytes");
          setState((prev) => ({
            ...prev,
            isTranscribing: false,
            error: "Aufnahme zu kurz. Bitte sprich mindestens 1-2 Sekunden.",
          }));
          resolve(null);
          return;
        }

        try {
          // Audio zu Base64 konvertieren (FileReader-basiert, robust für große Dateien)
          console.log("[Voice] Converting to base64...");
          const base64 = await blobToBase64(audioBlob);
          console.log("[Voice] Base64 length:", base64.length);

          // Determine clean MIME type for backend
          const cleanMime = mediaRecorder.mimeType.split(";")[0] || "audio/webm";

          // An Backend senden
          console.log("[Voice] Sending to backend for transcription...");
          const result = await transcribeMutation.mutateAsync({
            audioBase64: base64,
            mimeType: cleanMime,
            clientId,
          });

          console.log("[Voice] Transcription result:", result);

          if (result.success && result.text) {
            setState((prev) => ({
              ...prev,
              isTranscribing: false,
              error: null,
            }));
            resolve(result.text);
          } else {
            console.warn("[Voice] Transcription returned no text:", result.error);
            setState((prev) => ({
              ...prev,
              isTranscribing: false,
              error: result.error || "Transkription fehlgeschlagen.",
            }));
            resolve(null);
          }
        } catch (err) {
          console.error("[Voice] Transcription error:", err);
          setState((prev) => ({
            ...prev,
            isTranscribing: false,
            error: "Spracherkennung fehlgeschlagen. Bitte versuche es erneut.",
          }));
          resolve(null);
        }
      };

      mediaRecorder.stop();
    });
  }, [clientId, transcribeMutation]);

  /**
   * Bricht die Aufnahme ab ohne zu transkribieren.
   */
  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      mediaRecorder.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    chunksRef.current = [];
    setState({
      isRecording: false,
      isTranscribing: false,
      error: null,
      duration: 0,
    });
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
